<template>
  <div class="chart-card">
    <div class="chart-header">
      <div class="chart-title-block">
        <span class="chart-kicker">SCORE</span>
        <h3 class="chart-title">Salud Competitiva</h3>
      </div>
    </div>

    <div v-if="loading" class="chart-loading">Cargando…</div>
    <div v-else class="gauge-container">
      <!-- Gauge SVG -->
      <svg viewBox="0 0 200 130" class="gauge-svg">
        <!-- Arco de fondo -->
        <path
          d="M 20 110 A 80 80 0 0 1 180 110"
          fill="none"
          stroke="var(--color-border-strong)"
          stroke-width="14"
          stroke-linecap="round"
        />
        <!-- Arco activo coloreado -->
        <path
          d="M 20 110 A 80 80 0 0 1 180 110"
          fill="none"
          :stroke="data.color"
          stroke-width="14"
          stroke-linecap="round"
          :stroke-dasharray="`${arcLength} 999`"
          style="transition: all 0.6s ease-out;"
        />
        <!-- Aguja -->
        <line
          x1="100" y1="110"
          :x2="100 + 65 * Math.cos(needleAngle)"
          :y2="110 + 65 * Math.sin(needleAngle)"
          stroke="var(--color-text)"
          stroke-width="2"
          stroke-linecap="round"
          style="transition: all 0.6s ease-out;"
        />
        <circle cx="100" cy="110" r="6" fill="var(--color-text)" />
        <circle cx="100" cy="110" r="3" :fill="data.color" />
      </svg>

      <!-- Valor -->
      <div class="gauge-value-block">
        <div class="gauge-value" :style="{ color: data.color }">{{ data.score }}</div>
        <div class="gauge-nivel" :style="{ color: data.color }">{{ data.nivel?.toUpperCase() }}</div>
      </div>

      <!-- Breakdown -->
      <div class="gauge-breakdown">
        <div class="bd-row">
          <span class="bd-label">Productos competitivos</span>
          <span class="bd-value">{{ data.breakdown?.competitivos?.toLocaleString() }}</span>
        </div>
        <div class="bd-row">
          <span class="bd-label">Donde ganamos</span>
          <span class="bd-value bd-good">{{ data.breakdown?.ganadores?.toLocaleString() }} ({{ data.breakdown?.pct_ganadores }}%)</span>
        </div>
        <div class="bd-row">
          <span class="bd-label">Pérdida grave (&gt;20%)</span>
          <span class="bd-value bd-bad">{{ data.breakdown?.perdiendo_grave?.toLocaleString() }} ({{ data.breakdown?.pct_perdida_grave }}%)</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineProps } from 'vue';
import axios from 'axios';

const props = defineProps({
  vista: { type: String, default: 'web' }
});

const API = 'https://dash-visaovip.kompara.tech/api';
const getH = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token_precios') } });

const data = ref({ score: 0, nivel: '', color: '#ccc', breakdown: {} });
const loading = ref(true);

// Arco semicircular: longitud total = π * radio = π * 80 ≈ 251
const arcLength = computed(() => (data.value.score / 100) * 251);

// Aguja: 0 score = 180° (apunta a la izquierda), 100 score = 0° (a la derecha)
// En SVG, ángulos crecen en sentido horario, 0 = derecha
const needleAngle = computed(() => {
  // Mapear score 0-100 a ángulo en radianes: 0 → π (izquierda), 100 → 0 (derecha)
  // Pero queremos arco superior, entonces ángulos van de π a 2π (en SVG y abajo positivo)
  const t = data.value.score / 100;
  return Math.PI + t * Math.PI;
});

const load = async () => {
  loading.value = true;
  try {
    const res = await axios.get(`${API}/stats/score-salud`, {
      ...getH(), params: { vista: props.vista }
    });
    data.value = res.data;
  } catch (e) {
    console.error('Error score-salud:', e);
  } finally {
    loading.value = false;
  }
};

watch(() => props.vista, load);
onMounted(load);
</script>

<style scoped>
.chart-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.chart-header {
  padding: 10px 14px;
  background: var(--color-surface-3);
  border-bottom: 1px solid var(--color-accent-border);
}
.chart-kicker {
  display: block;
  font-family: var(--font-mono);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.chart-title {
  margin: 2px 0 0;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
}
.chart-loading {
  padding: 60px 20px;
  text-align: center;
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: 11px;
}

.gauge-container {
  padding: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.gauge-svg {
  width: 100%;
  max-width: 220px;
  height: auto;
}
.gauge-value-block {
  text-align: center;
  margin-top: -16px;
}
.gauge-value {
  font-family: var(--font-mono);
  font-size: 32px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  line-height: 1;
}
.gauge-nivel {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  margin-top: 4px;
}
.gauge-breakdown {
  width: 100%;
  border-top: 1px solid var(--color-border);
  padding-top: 10px;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.bd-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: var(--font-mono);
  font-size: 10.5px;
}
.bd-label {
  color: var(--color-text-muted);
}
.bd-value {
  color: var(--color-text);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.bd-good { color: var(--color-success-text); }
.bd-bad { color: var(--color-danger-text); }
</style>